import './Kidsprogram.css';
import React, { useState } from 'react';
import {
  Container, TextField, Button, MenuItem,
  Typography, Grid, Box, Card, CardContent
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Header from '../Components/Header';
import Footer from './Footer';

// ✅ Custom images
import python from '../images/python.png';
import arts from '../images/paint-palette.png';
import genai from '../images/chat-app.png';

const KidsProgrammingPage = () => {
  const [formData, setFormData] = useState({
    studentName: '',
    parentName: '',
    email: '',
    phone: '',
    program: '',
    tentativeStartDate: '',
    schedule: '',
    numberOfClasses: '',
    preferredLanguage: '',
    tentativePreferredTime: '',
    timezone: '',
  });

  const [errors, setErrors] = useState({});

  const programs = ['Art', 'Python Programming', 'Gen AI'];
  const schedules = ['Weekly', 'Monthly'];
  const preferredLanguages = ['English', 'Tamil', 'Telugu'];
  const numberOfClasses = Array.from({ length: 16 }, (_, i) => i + 1);
  const timezones = [
    { label: 'EST (Eastern Standard Time, UTC-5)', value: 'UTC-5' },
    { label: 'CST (Central Standard Time, UTC-6)', value: 'UTC-6' },
    { label: 'MST (Mountain Standard Time, UTC-7)', value: 'UTC-7' },
    { label: 'PST (Pacific Standard Time, UTC-8)', value: 'UTC-8' },
    { label: 'AKST (Alaska Standard Time, UTC-9)', value: 'UTC-9' },
    { label: 'HST (Hawaii Standard Time, UTC-10)', value: 'UTC-10' },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: '' });
  };

  const validateForm = () => {
    let newErrors = {};
    if (!formData.studentName) newErrors.studentName = 'Student name is required';
    if (!formData.parentName) newErrors.parentName = 'Parent name is required';
    if (!formData.email) newErrors.email = 'Email is required';
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email';
    if (!formData.phone) newErrors.phone = 'Phone number is required';
    if (!formData.program) newErrors.program = 'Program is required';
    if (!formData.tentativeStartDate) newErrors.tentativeStartDate = 'Start date is required';
    if (!formData.schedule) newErrors.schedule = 'Schedule is required';
    if (!formData.numberOfClasses) newErrors.numberOfClasses = 'Required';
    if (!formData.preferredLanguage) newErrors.preferredLanguage = 'Required';
    if (!formData.tentativePreferredTime) newErrors.tentativePreferredTime = 'Required';
    if (!formData.timezone) newErrors.timezone = 'Timezone is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ✅ handleSubmit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      // Combine time and timezone
      const combinedTime = `${formData.tentativePreferredTime} ${formData.timezone}`;

      const payload = {
        ...formData,
        tentativePreferredTime: combinedTime,
        timezone: undefined, // drop separate field
      };

      try {
        const response = await fetch('https://globalsgtech.com/api/api/student-form', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        const result = await response.json();

        if (response.ok) {
          console.log(result.message);
          alert('Thank you for registering! Our team will review your details and get back to you soon.');
          setFormData({
            studentName: '',
            parentName: '',
            email: '',
            phone: '',
            program: '',
            tentativeStartDate: '',
            schedule: '',
            numberOfClasses: '',
            preferredLanguage: '',
            tentativePreferredTime: '',
            timezone: '',
          });
        } else {
          console.error(result.message);
          alert('Error submitting form');
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        alert('Error submitting form');
      }
    }
  };

  return (
    <>
      <Header />

      {/* Top Section */}
      <div className="kids-header">
        <h1>Youth & Kids Programming</h1>
        <p>
          Ignite your child's passion for technology with fun and interactive programming courses designed for young learners. 
          Explore the world of coding in an engaging and educational way
        </p>
        <div className="offer-box">
          🎉 Special Offer: First Session Free! <br />
          <span>Register now and enjoy your first class at no cost.</span>
        </div>
      </div>

      <Container maxWidth="lg">
        {/* Course Cards */}
        <Grid container spacing={3} className="course-section">
          <Grid item xs={12} md={4}>
            <Card className="course-card">
              <div className="promo-badge">1st Session Free</div>
              <CardContent>
                <img src={arts} alt="Art" className="course-icon" />
                <h2>Art</h2>
                <p className="price">$149 / 3 months</p>
                <p className="price">6 - 12 yrs</p>
                <p>Unleash your creativity</p>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card className="course-card">
              <div className="promo-badge">1st Session Free</div>
              <CardContent>
                <img src={python} alt="Python Programming" className="course-icon" />
                <h2>Python Programming</h2>
                <p className="price">$199 / 3 months</p>
                <p className="price">9 - 16 yrs</p>
                <p>Learn coding basics</p>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card className="course-card">
              <div className="promo-badge">1st Session Free</div>
              <CardContent>
                <img src={genai} alt="Gen AI" className="course-icon" />
                <h2>Gen AI</h2>
                <p className="price">$249 / 3 months</p>
                <p className="price">9 - 16 yrs</p>
                <p>Explore AI tools</p>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Registration Form */}
        <Box className="form-box">
          <Typography variant="h6" className="form-title">
            <CheckCircleIcon color="success" style={{ marginRight: '8px' }} />
            Your first class is free — no payment required at signup
          </Typography>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {/* Student & Parent */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label="Student's Name *" name="studentName"
                  value={formData.studentName} onChange={handleChange}
                  error={!!errors.studentName} helperText={errors.studentName}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label="Parent's Name *" name="parentName"
                  value={formData.parentName} onChange={handleChange}
                  error={!!errors.parentName} helperText={errors.parentName}
                />
              </Grid>

              {/* Email & Phone */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label="Email *" name="email"
                  value={formData.email} onChange={handleChange}
                  error={!!errors.email} helperText={errors.email}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label="Phone Number *" name="phone"
                  value={formData.phone} onChange={handleChange}
                  error={!!errors.phone} helperText={errors.phone}
                />
              </Grid>

              {/* Program & Date */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select fullWidth label="Program *" name="program"
                  value={formData.program} onChange={handleChange}
                  error={!!errors.program} helperText={errors.program}
                >
                  {programs.map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  type="date" fullWidth label="Tentative Start Date *" name="tentativeStartDate"
                  value={formData.tentativeStartDate} onChange={handleChange}
                  error={!!errors.tentativeStartDate} helperText={errors.tentativeStartDate}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Schedule & Classes */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select fullWidth label="Schedule *" name="schedule"
                  value={formData.schedule} onChange={handleChange}
                  error={!!errors.schedule} helperText={errors.schedule}
                >
                  {schedules.map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
              <TextField
  label="Number of Classes *"
  type="number"
  name="numberOfClasses"
  value={formData.numberOfClasses}
  onChange={handleChange}
  inputProps={{ min: 1, max: 16 }}
  fullWidth
  margin="normal"
  error={!!errors.numberOfClasses}
  helperText={errors.numberOfClasses}
/>
                {/* <TextField
                  select fullWidth label="Number of Classes *" name="numberOfClasses"
                  value={formData.numberOfClasses} onChange={handleChange}
                  error={!!errors.numberOfClasses} helperText={errors.numberOfClasses}
                >
                  {numberOfClasses.map((num) => (
                    <MenuItem key={num} value={num}>{num}</MenuItem>
                  ))}
                </TextField> */}
              </Grid>

              {/* Language & Time */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select fullWidth label="Preferred Language *" name="preferredLanguage"
                  value={formData.preferredLanguage} onChange={handleChange}
                  error={!!errors.preferredLanguage} helperText={errors.preferredLanguage}
                >
                  {preferredLanguages.map((lang) => (
                    <MenuItem key={lang} value={lang}>{lang}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  type="time" fullWidth label="Preferred Time *" name="tentativePreferredTime"
                  value={formData.tentativePreferredTime} onChange={handleChange}
                  error={!!errors.tentativePreferredTime} helperText={errors.tentativePreferredTime}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  select fullWidth label="Timezone *" name="timezone"
                  value={formData.timezone} onChange={handleChange}
                  error={!!errors.timezone} helperText={errors.timezone}
                >
                  {timezones.map((tz) => (
                    <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Submit */}
              <Grid item xs={12}>
                <Button type="submit" variant="contained" className="register-btn" fullWidth>
                  REGISTER
                </Button>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Container>

      <Footer />
    </>
  );
};

export default KidsProgrammingPage;
